import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MedicationFormData } from '@/components/medication/types/medication';
import AnimatedContainer from '@/components/ui-elements/AnimatedContainer';

// Define medication form options
const MEDICATION_FORMS = [
  { value: 'tablet', label: 'Tablet' },
  { value: 'capsule', label: 'Capsule' },
  { value: 'liquid', label: 'Liquid' },
  { value: 'injection', label: 'Injection' },
  { value: 'inhaler', label: 'Inhaler' },
  { value: 'patch', label: 'Patch' },
  { value: 'cream', label: 'Cream/Ointment' },
  { value: 'drops', label: 'Drops' },
  { value: 'spray', label: 'Spray' },
  { value: 'other', label: 'Other' }
];

interface BasicDetailsStepProps {
  formData: MedicationFormData;
  updateFormData: (fieldName: keyof MedicationFormData, value: any) => void;
}

/**
 * Step 1: Collect basic medication details like name, dosage and form
 */
const BasicDetailsStep: React.FC<BasicDetailsStepProps> = ({ formData, updateFormData }) => {
  return (
    <AnimatedContainer animation="fade-in" className="space-y-6">
      <div className="space-y-3">
        <Label htmlFor="medication-name" className="text-sm font-medium">
          Medication Name <span className="text-red-500">*</span>
        </Label>
        <Input
          id="medication-name"
          type="text"
          value={formData.name}
          onChange={(e) => updateFormData('name', e.target.value)}
          placeholder="Enter medication name"
          className="w-full"
          autoFocus
          aria-required="true"
        />
      </div>

      <div className="space-y-3">
        <Label htmlFor="medication-dosage" className="text-sm font-medium">
          Dosage <span className="text-red-500">*</span>
        </Label>
        <Input
          id="medication-dosage"
          type="text"
          value={formData.dosage}
          onChange={(e) => updateFormData('dosage', e.target.value)}
          placeholder="e.g., 50mg, 5ml, etc."
          className="w-full"
          aria-required="true"
        />
      </div>

      <div className="space-y-3">
        <Label className="text-sm font-medium">
          Form <span className="text-red-500">*</span>
        </Label>
        <div className="grid grid-cols-2 gap-2">
          {MEDICATION_FORMS.map((form) => (
            <div
              key={form.value}
              className={`h-12 flex items-center justify-center rounded-md transition-colors px-3 ${formData.form === form.value 
                ? 'bg-purple-600 text-white border border-purple-600' 
                : 'bg-gray-50 border border-gray-200 dark:bg-gray-900/10 dark:border-gray-800 hover:bg-gray-100 dark:hover:bg-gray-900/20 cursor-pointer'}`}
              onClick={() => updateFormData('form', form.value)}
              role="button"
              aria-pressed={formData.form === form.value}
              tabIndex={0}
            >
              {form.label}
            </div>
          ))}
        </div>
      </div>
    </AnimatedContainer>
  );
};

export default BasicDetailsStep;
